import Card from '@/components/common/Card'
import FormField from '@/components/common/FormField'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import { WfhService, type ICreateWfhPayload } from '@/services/WFH/WfhService'
import { showToastNotification } from '@/utility/helper'
import { RoutesLink } from '@/utility/RouteLinks'
import { errorTypes } from '@/utility/validation'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { ThrowError } from '@/api/callApi'

const ADDWFH: React.FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<any>({
        defaultValues: {
            duration: "FULL_DAY",
            numberOfDays: 1,
        }
    });

    const checkInDate = watch("checkInDate");
    const checkOutDate = watch("checkOutDate");
    const duration = watch("duration");

    // Auto-calculate numberOfDays and sync checkOutDate for half days
    useEffect(() => {
        if (duration === "FIRST_HALF" || duration === "SECOND_HALF") {
            setValue("numberOfDays", 0.5);
            if (checkInDate) {
                setValue("checkOutDate", checkInDate);
            }
        } else if (checkInDate && checkOutDate) {
            const start = new Date(checkInDate);
            const end = new Date(checkOutDate);
            const diffTime = end.getTime() - start.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            setValue("numberOfDays", diffDays > 0 ? diffDays : 1);
        }
    }, [checkInDate, checkOutDate, duration, setValue]);

    // Ensure checkOutDate is not prior to checkInDate
    useEffect(() => {
        if (checkInDate && checkOutDate) {
            if (new Date(checkOutDate) < new Date(checkInDate)) {
                setValue("checkOutDate", checkInDate);
            }
        }
    }, [checkInDate, checkOutDate, setValue]);

    const onSubmit = async (data: any) => {
        try {
            setIsLoading(true);
            const payload: ICreateWfhPayload = {
                checkInDate: data.checkInDate || watch("checkInDate"),
                checkOutDate: data.checkOutDate || watch("checkOutDate"),
                duration: data.duration,
                numberOfDays: Number(data.numberOfDays) || watch("numberOfDays"),
                reason: data.reason,
            }

            const response = await WfhService.createWfh(payload);

            if (response?.status) {
                showToastNotification("success", response?.message || "WFH applied successfully")
                navigate(RoutesLink.wfhRequest || -1 as any)
            } else {
                showToastNotification("danger", response?.message || "Failed to submit WFH request")
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading
                    title="Apply for Work From Home"
                    isBackButtonVisible
                    onClick={() => navigate(-1)}
                />
                <Card className='overflow-visible'>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                {...register("duration", { ...errorTypes.required('Duration') })}
                                type="select"
                                label="Duration"
                                value={watch("duration")}
                                options={[
                                    { value: 'select', label: 'Select Duration' },
                                    { value: 'FULL_DAY', label: 'Full Day' },
                                    { value: 'FIRST_HALF', label: 'First Half' },
                                    { value: 'SECOND_HALF', label: 'Second Half' },
                                ]}
                                error={errors.duration?.message as string}
                            />

                            <FormField
                                {...register("checkInDate", { ...errorTypes.required('Check-In Date') })}
                                type="date"
                                label="Check-In Date"
                                error={errors.checkInDate?.message as string}
                            />

                            <FormField
                                {...register("checkOutDate", { ...errorTypes.required('Check-Out Date') })}
                                type="date"
                                label="Check-Out Date"
                                min={checkInDate}
                                disabled={duration === "FIRST_HALF" || duration === "SECOND_HALF"}
                                error={errors.checkOutDate?.message as string}
                            />

                            <FormField
                                {...register("numberOfDays", { ...errorTypes.required('Number of Days') })}
                                type="number"
                                step="0.5"
                                label="Number of Days"
                                placeholder="Auto-calculated"
                                error={errors.numberOfDays?.message as string}
                                readOnly
                            />

                            <FormField
                                {...register("reason", { ...errorTypes.required('Reason for WFH') })}
                                type="text"
                                label="Reason"
                                placeholder="Enter reason for WFH"
                                error={errors.reason?.message as string}
                            />
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Submitting..." : "Apply WFH"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default ADDWFH